import React from "react";
import Card from "./_Card";
import Stepper from "../../../components/profile/Stepper";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Button from "../../../components/ui/Button";
import ProfileSidebar from "../../../components/reusable components/ProfileSidebar";
import { Option, getRoles } from "../../../lib/api";
import { valueExistsInOptions } from "./selectHelpers";
import {
  getOrganizationStats,
  putOrganizationStats,
  OrganizationStats,
} from "../../../api/profile";
import {
  createTeam,
  inviteToTeam,
  getTeamList,
  setActiveTeam,
  deleteTeam,
  deleteTeamMember,
} from "../../../api/dashboard";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// ---- Types from team list API ----
type CollabTeamMember = {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
};

type CollabTeam = {
  team_id: number;
  team_name: string;
  members?: CollabTeamMember[];
  is_active?: boolean;
};

export default function Team() {
  const navigate = useNavigate();

  const [roleOpts, setRoleOpts] = React.useState<Option[]>([]);

  const [employees, setEmployees] = React.useState("");
  const [board, setBoard] = React.useState("");
  const [volunteers, setVolunteers] = React.useState("");
  const [hasPitchDeck, setHasPitchDeck] = React.useState("");
  const [companyStage, setCompanyStage] = React.useState("");

  const [creatingTeam, setCreatingTeam] = React.useState(false);
  const [newTeamName, setNewTeamName] = React.useState("");

  const [teams, setTeams] = React.useState<CollabTeam[]>([]);
  const [teamsLoading, setTeamsLoading] = React.useState(false);
  const [activeTeamId, setActiveTeamId] = React.useState<number | null>(null);
  const safeHasPitchDeck = ["Yes", "No"].includes(hasPitchDeck) ? hasPitchDeck : "";
  const safeCompanyStage = ["Idea", "Prototype", "Early revenue", "Scaling", "Mature"].includes(
    companyStage
  )
    ? companyStage
    : "";

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [roles, stats] = await Promise.all([
          getRoles(),
          getOrganizationStats(),
        ]);
        if (!active) return;

        setRoleOpts(roles);

        setBoard(
          stats.Number_of_Board_Members !== null &&
            stats.Number_of_Board_Members !== undefined
            ? String(stats.Number_of_Board_Members)
            : ""
        );
        setEmployees(
          stats.Number_of_Employee !== null &&
            stats.Number_of_Employee !== undefined
            ? String(stats.Number_of_Employee)
            : ""
        );
        setVolunteers(
          stats.Number_of_Volunteers !== null &&
            stats.Number_of_Volunteers !== undefined
            ? String(stats.Number_of_Volunteers)
            : ""
        );

        if (stats.extra_details) {
          const rawPitchDeck = stats.extra_details.has_pitch_deck || "";
          const rawCompanyStage = stats.extra_details.company_stage || "";

          setHasPitchDeck(
            valueExistsInOptions(rawPitchDeck, [
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" },
            ])
              ? rawPitchDeck
              : ""
          );
          setCompanyStage(
            valueExistsInOptions(rawCompanyStage, [
              { value: "Idea", label: "Idea" },
              { value: "Prototype", label: "Prototype" },
              { value: "Early revenue", label: "Early revenue" },
              { value: "Scaling", label: "Scaling" },
              { value: "Mature", label: "Mature" },
            ])
              ? rawCompanyStage
              : ""
          );
        }

        await loadTeamsSafe();
      } catch (e: any) {
        const msg =
          e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail|| 
          "Failed to load team data";
        toast.error(msg);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const loadTeamsSafe = async () => {
    try {
      setTeamsLoading(true);
      const raw = await getTeamList();

      let list: CollabTeam[] = [];
      let activeId: number | null = null;

      if (raw && Array.isArray((raw as any).teams)) {
        list = (raw as any).teams as CollabTeam[];
      } else if (Array.isArray(raw)) {
        list = raw as CollabTeam[];
      }
      if (!activeId && list.length) {
        const activeTeam = list.find((t) => t.is_active);
        if (activeTeam) activeId = activeTeam.team_id;
      }

      setTeams(list);
      setActiveTeamId(activeId ?? null);
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail|| 
        "Failed to load teams";
      toast.error(msg);
    } finally {
      setTeamsLoading(false);
    }
  };

  // --- Helpers for stats numeric conversion ---
  const toIntOrNull = (v: string) => {
    const n = parseInt(v.replace(/[^\d-]/g, ""), 10);
    return Number.isFinite(n) ? n : null;
  };

  const buildPayload = (): OrganizationStats => ({
    Number_of_Board_Members: toIntOrNull(board),
    Number_of_Employee: toIntOrNull(employees),
    Number_of_Volunteers: toIntOrNull(volunteers),
    extra_details: {
      has_pitch_deck: hasPitchDeck || undefined,
      company_stage: companyStage || undefined,
    },
  });

  const handleSave = async () => {
    try {
      await putOrganizationStats(buildPayload());
      toast.success("Team & structure saved");
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail||
        "Failed to save team & structure";
      toast.error(msg);
    }
  };

  // ---- Create team handler ----
  const handleCreateTeam = async () => {
  const trimmed = newTeamName.trim();
  if (!trimmed) {
    toast.error("Team name is required");
    return;
  }

  try {
    setCreatingTeam(true);

    await createTeam(trimmed);

    toast.success("Team created successfully");
    setNewTeamName("");
    await loadTeamsSafe();

  } catch (e: any) {
    const msg =
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      e?.response?.data?.detail ||
      "Failed to create team";
    toast.error(msg);
  } finally {
    setCreatingTeam(false);
  }
};

  // ---- Set active / deactivate team handler ----
  const handleSetActive = async (teamId: number | null) => {
    try {
      const res = await setActiveTeam(teamId);

      await loadTeamsSafe();

      const msgFromApi = res?.message as string | undefined;
      toast.success(
        msgFromApi ||
          (teamId ? "Team set active successfully" : "Team deactivated successfully")
      );
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail|| 
        "Failed to set active team";
      toast.error(msg);
    }
  };

  // ---- Delete team handler ----
  const handleDeleteTeam = async (teamId: number) => {
  const confirmed = window.confirm(
    "Are you sure you want to delete this team? This action cannot be undone."
  );
  if (!confirmed) return;

  try {
    await deleteTeam(teamId);

    toast.success("Team deleted successfully");

    if (activeTeamId === teamId) {
      setActiveTeamId(null);
    }

    await loadTeamsSafe();
  } catch (e: any) {
    console.error("Delete team error:", e);

    if (e?.response?.status === 403) {
      toast.error("You do not have permission to delete this team.");
      return;
    }

    if (e?.response?.status === 404) {
      toast.error("Team not found.");
      return;
    }

    const msg =
      e?.response?.data?.detail ||
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      "Failed to delete team";

    toast.error(msg);
  }
};
  const handleDeleteMember = async (teamId: number, memberId: number) => {
    const confirmed = window.confirm(
      "Are you sure you want to remove this member from the team?"
    );
    if (!confirmed) return;

    try {
      await deleteTeamMember(memberId);

      setTeams((prev) =>
        prev.map((t) =>
          t.team_id === teamId
            ? {
                ...t,
                members: (t.members || []).filter((m) => m.id !== memberId),
              }
            : t
        )
      );

      toast.success("Team member removed");
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail|| 
        "Failed to delete team member";
      toast.error(msg);
    }
  };

  const goNext = async () => {
    await handleSave();
    navigate("/dashboard/profile/funding");
  };

  return (
    <div className="flex min-h-screen gap-5 px-3">
      <ProfileSidebar isProfileRoute={true} />

      <div className="flex-1 min-w-0 overflow-y-auto">
        <div className="h-full">
          <Card>
            <div className="px-6 py-5">
              <Stepper active="team" />
            </div>

            <div className="px-6 pb-6 space-y-6">
              <div className="text-[20px] font-semibold">Team &amp; Structure</div>

              {/* --- Organization stats --- */}
              <div className="grid md:grid-cols-3 gap-x-20 gap-y-6">
                <div>
                  <div className="text-sm font-semibold mb-1">
                    Number of Board Members
                  </div>
                  <Input
                    value={board}
                    onChange={(e) =>
                      setBoard(e.target.value.replace(/\D/g, ""))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">
                    Number of Employee
                  </div>
                  <Input
                    value={employees}
                    onChange={(e) =>
                      setEmployees(e.target.value.replace(/\D/g, ""))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">
                    Number of Volunteers
                  </div>
                  <Input
                    value={volunteers}
                    onChange={(e) =>
                      setVolunteers(e.target.value.replace(/\D/g, ""))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Has Pitch Deck</div>
                  <Select
                    value={safeHasPitchDeck}
                    onChange={(e) => setHasPitchDeck(e.target.value)}
                  >
                    <option value="" disabled>Select pitch deck status</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Select>
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Company Stage</div>
                  <Select
                    value={safeCompanyStage}
                    onChange={(e) => setCompanyStage(e.target.value)}
                  >
                    <option value="" disabled>Select company stage</option>
                    <option value="Idea">Idea</option>
                    <option value="Prototype">Prototype</option>
                    <option value="Early revenue">Early revenue</option>
                    <option value="Scaling">Scaling</option>
                    <option value="Mature">Mature</option>
                  </Select>
                </div>
              </div>

              <hr className="border-[#E5E7EB]" />

              {/* ---- Teams management ---- */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-[20px] font-semibold">Teams</div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="New team name"
                      value={newTeamName}
                      onChange={(e) => setNewTeamName(e.target.value)}
                    />
                    <Button
                      className="min-w-[120px]"
                      onClick={handleCreateTeam}
                      disabled={creatingTeam}
                    >
                      {creatingTeam ? "Creating…" : "Create team"}
                    </Button>
                  </div>
                </div>

                <div className="text-xs text-[#777B7F]">
                  Create a team, then invite members directly from each card.
                </div>

                {teamsLoading ? (
                  <div className="text-sm text-[#6b7280]">Loading teams…</div>
                ) : teams.length ? (
                  <div className="grid md:grid-cols-3 gap-4">
                    {teams.map((t) => {
                      const teamId = t.team_id;
                      const isActive = activeTeamId === teamId || !!t.is_active;

                      return (
                        <TeamCard
                          key={teamId}
                          team={t}
                          isActive={isActive}
                          onToggleActive={() =>
                            handleSetActive(isActive ? null : teamId)
                          }
                          onDelete={() => handleDeleteTeam(teamId)}
                          onDeleteMember={(memberId) =>
                            handleDeleteMember(teamId, memberId)
                          }
                            reloadTeams={loadTeamsSafe} // 👈 add thi
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-[#6b7280]">
                    No teams yet. Create your first team to start collaborating.
                  </div>
                )}
              </div>

              <hr className="border-[#E5E7EB]" />

              <div className="flex justify-end gap-3">
                {/* <button className="h-10 px-4 rounded-[10px] border bg-white text-sm"> */}
                  {/* Skip */}
                {/* </button> */}
                <Button className="min-w-[96px]" onClick={handleSave}>
                  Save
                </Button>
                <Button className="min-w-[96px] gap-2" onClick={goNext}>
                  Next
                  <span className="material-icons">arrow_right_alt</span>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ---- Team card with per-card invite ----
type TeamCardProps = {
  team: CollabTeam;
  isActive: boolean;
  onToggleActive: () => void;
  onDelete: () => void;
  onDeleteMember: (memberId: number) => void;
  reloadTeams: () => Promise<void>;
};

const TeamCard: React.FC<TeamCardProps> = ({
  team,
  isActive,
  onToggleActive,
  onDelete,
  onDeleteMember,
  reloadTeams,
}) => {
  const [inviteEmail, setInviteEmail] = React.useState("");
  const [inviteRole, setInviteRole] =
    React.useState<"admin" | "editor" | "viewer">("viewer");
  const [sending, setSending] = React.useState(false);

  const resolvedTeamId = team.team_id;

  const handleSendInvite = async () => {
  const cleanedEmail = inviteEmail.trim();
  if (!cleanedEmail) {
    toast.error("Invite email is required");
    return;
  }

  try {
    setSending(true);

    await inviteToTeam({
      email: cleanedEmail,
      team_id: resolvedTeamId,
      role: inviteRole,
    });

    toast.success("Invite sent successfully");
    setInviteEmail("");
    await reloadTeams();

  } catch (e: any) {
    const msg =
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      e?.response?.data?.detail ||
      "Failed to send invite";
    toast.error(msg);
  } finally {
    setSending(false);
  }
};

  const memberCount = team.members?.length ?? 0;

  return (
    <div
      className={`rounded-[16px] border p-4 flex flex-col gap-3 ${
        isActive ? "border-[#2f8c55] bg-[#C0EFC0]/25" : "border-[#E5E7EB] bg-white"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
  <div className="flex flex-col gap-1 min-w-0">
    <div
      className="font-semibold text-sm truncate w-full"
      title={team.team_name}
    >
      {team.team_name}
    </div>
    <span className="text-[11px] text-[#6B7280]">
      {memberCount} member{memberCount === 1 ? "" : "s"}
    </span>
  </div>

        <button
          type="button"
          onClick={onDelete}
          className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-red-50"
          title="Delete team"
        >
          <span className="material-icons text-[18px] text-[#c34e46]">
            delete_outline
          </span>
        </button>
      </div>

      {/* Active status row */}
      <div className="flex items-center justify-between mt-1">
        <span
          className={`text-[11px] px-2 py-[3px] rounded-full ${
            isActive
              ? "bg-[#DCFCE7] text-[#166534]"
              : "bg-[#F3F4F6] text-[#4B5563]"
          }`}
        >
          {isActive ? "Active team" : "Inactive"}
        </span>
        <Button className="h-8 px-3 text-xs" onClick={onToggleActive}>
          {isActive ? "Deactivate" : "Set active"}
        </Button>
      </div>

      {/* Team members list */}
      {team.members && team.members.length > 0 && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-semibold text-[#374151]">
              Team members
            </div>
          </div>

          <div className="border border-[#E5E7EB] rounded-lg bg-[#F9FAFB] max-h-40 overflow-y-auto">
            <ul className="divide-y divide-[#E5E7EB]">
              {team.members.map((m) => (
                <li
                  key={m.id}
                  className="flex items-start justify-between px-2 py-2 gap-2"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] font-medium text-[#111827] truncate">
                        {m.name}
                      </span>
                      <span className="text-[10px] px-2 py-[1px] rounded-full bg-[#E0F2FE] text-[#0369A1] capitalize">
                        {m.role}
                      </span>
                    </div>
                    <div className="text-[10px] text-[#6b7280] leading-tight truncate">
                      {m.email}
                    </div>
                    <div
                      className={`text-[10px] mt-0.5 capitalize ${
                        m.status?.toLowerCase() === "accepted"
                          ? "text-[#16A34A]"
                          : "text-[#D97706]"
                      }`}
                    >
                      {m.status}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => onDeleteMember(m.id)}
                    className="mt-1 h-5 w-5 flex items-center justify-center rounded-full hover:bg-red-50 flex-shrink-0"
                    title="Remove member"
                  >
                    <span className="material-icons text-[14px] text-[#c34e46]">
                      close
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="mt-auto">
        <hr className="border-[#E5E7EB] my-2" />

        {/* Invite section */}
        <div className="space-y-2 ">
          <div className="text-xs font-semibold">Invite member</div>
          <Input
            placeholder="member@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <Select
              className="flex-1"
              value={inviteRole}
              onChange={(e) =>
                setInviteRole(e.target.value as "admin" | "editor" | "viewer")
              }
            >
              <option value="" disabled>Select role</option>
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </Select>
            <Button
              className="h-9 px-3 text-xs"
              onClick={handleSendInvite}
              disabled={sending}
            >
              {sending ? "Sending…" : "Send invite"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
